import type { Scan } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { addScanToCart, loadCarts, removeLineFromCart } from "../../core/ops/cart-ops";
import { createPosCustomer, loadCustomerScans, loadCustomers } from "../../core/ops/customer-ops";
import { cartLineForScan } from "../../core/primitives/customer-cart";
import { errorText } from "../../core/primitives/errors";
import { failedLoad, loaded, loading } from "../../core/primitives/load-status";
import { shopEntryId } from "../../core/primitives/workspace";
import { CustomerQrController } from "../../sections/customer-qr/customer-qr-controller";
import { CustomerScansActions, CustomerScansState } from "./customer-scans-model";

export type CustomerScansDeps = Pick<
  AppDeps,
  | "client"
  | "authHandler"
  | "storeRepository"
  | "providersRepository"
  | "customersRepository"
  | "pendingCartsRepository"
>;

export interface CustomerScansParams {
  /** `?customer=<id>`; null shows the list. */
  readonly customerId: string | null;
}

export interface CustomerScansRouting {
  showCustomer(id: string): void;
  showAllCustomers(): void;
  editScan(scanId: string): void;
  /** The scanner, scanning on this counter customer's behalf. */
  scanFor(customerId: string): void;
  /** The carts page, with this cart brought into view. */
  openCart(cartId: string): void;
}

/** The customer list, or one customer's scans. One controller per selection (the route keys it by `customerId`). */
export class CustomerScansController extends SubtreeController implements CustomerScansActions {
  readonly state: CustomerScansState;
  readonly qr: CustomerQrController;
  #scansRequested = false;

  constructor(
    readonly params: CustomerScansParams,
    readonly deps: CustomerScansDeps,
    readonly routing: CustomerScansRouting,
  ) {
    super();
    this.state = new CustomerScansState(params.customerId);
    this.subtree.put(CustomerScansState, this.state);
    this.subtree.put(CustomerScansActions, this);
    this.qr = this.own(new CustomerQrController(this.subtree, deps));

    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      this.state.storeId.value = shopId;
      if (shopId === null) return;
      const repo = deps.customersRepository;
      if (repo.status.kind === "idle" || repo.isStale)
        void loadCustomers({ ...deps, shopId, locationId: deps.storeRepository.locationId });
      if (params.customerId !== null && !this.#scansRequested) {
        this.#scansRequested = true;
        void this.#loadScans(shopId, params.customerId);
        void this.#loadCart(shopId, params.customerId);
      }
    }, [deps.storeRepository]);

    this.sync(() => {
      this.state.openCarts.value = deps.pendingCartsRepository.count;
    }, [deps.pendingCartsRepository]);

    this.sync(() => {
      const repo = deps.customersRepository;
      this.state.customers.value = repo.customers;
      this.state.customersStatus.value = repo.status;
      this.state.selectedCustomer.value = params.customerId === null ? null : (repo.find(params.customerId) ?? null);
    }, [deps.customersRepository]);
  }

  selectCustomer = (id: string) => {
    this.routing.showCustomer(id);
  };

  showAllCustomers = () => {
    this.routing.showAllCustomers();
  };

  editScan = (scan: Scan) => {
    this.routing.editScan(scan.id);
  };

  retry = () => {
    const { shopId, locationId } = this.deps.storeRepository;
    if (shopId === null) return;
    if (this.state.customersStatus.value.kind === "error") void loadCustomers({ ...this.deps, shopId, locationId });
    if (this.params.customerId !== null) {
      void this.#loadScans(shopId, this.params.customerId);
      if (this.state.cartStatus.value.kind === "error") void this.#loadCart(shopId, this.params.customerId);
    }
  };

  addToCart = async (scan: Scan) => {
    const shopId = this.deps.storeRepository.shopId;
    const customerId = this.params.customerId;
    if (shopId === null || customerId === null || this.state.busyScanId.value !== null) return;
    if (this.state.cartStatus.value.kind !== "loaded") return;
    await this.#cartAction(scan.id, async () =>
      addScanToCart({
        ...this.deps,
        shopId,
        customerId,
        scanId: scan.id,
        openCartId: this.state.cart.value?.id ?? null,
      }),
    );
  };

  removeFromCart = async (scan: Scan) => {
    const line = cartLineForScan(this.state.cart.value ? [this.state.cart.value] : [], scan);
    if (!line || this.state.busyScanId.value !== null) return;
    await this.#cartAction(scan.id, () => removeLineFromCart({ ...this.deps, cart: line.cart, itemId: line.item.id }));
  };

  checkout = () => {
    const cart = this.state.cart.value;
    if (cart) this.routing.openCart(cart.id);
  };

  scanForCustomer = () => {
    if (this.params.customerId !== null) this.routing.scanFor(this.params.customerId);
  };

  dismissActionError = () => {
    this.state.actionError.value = null;
  };

  openNewCustomer = () => {
    this.state.newCustomer.value = { name: "", busy: false, error: null };
  };

  setNewCustomerName = (name: string) => {
    this.state.newCustomer.update((d) => (d ? { ...d, name, error: null } : d));
  };

  closeNewCustomer = () => {
    this.state.newCustomer.value = null;
  };

  createCustomer = async () => {
    const draft = this.state.newCustomer.value;
    const shopId = this.deps.storeRepository.shopId;
    if (!draft || draft.busy || shopId === null) return;
    if (!draft.name.trim()) {
      this.state.newCustomer.value = { ...draft, error: "Give the customer a name." };
      return;
    }
    this.state.newCustomer.value = { ...draft, busy: true, error: null };
    const r = await createPosCustomer({ ...this.deps, shopId, name: draft.name });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.newCustomer.value = { ...draft, busy: false, error: errorText(r.errors) };
      return;
    }
    this.state.newCustomer.value = null;
    this.routing.showCustomer(r.value.id);
  };

  showQr = () => {
    const { shopId, locationId } = this.deps.storeRepository;
    if (shopId !== null) this.qr.open(shopEntryId(shopId, locationId));
  };

  async #loadScans(shopId: string, customerId: string): Promise<void> {
    this.state.scansStatus.value = loading;
    const r = await loadCustomerScans({
      ...this.deps,
      shopId,
      locationId: this.deps.storeRepository.locationId,
      customerId,
    });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.scansStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.scans.value = r.value;
    this.state.scansStatus.value = loaded;
  }

  async #loadCart(shopId: string, customerId: string): Promise<void> {
    this.state.cartStatus.value = loading;
    const r = await loadCarts({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.cartStatus.value = failedLoad(r.errors);
      return;
    }
    this.state.cart.value = r.value.find((c) => c.status === "OPEN" && c.user.id === customerId) ?? null;
    this.state.cartStatus.value = loaded;
  }

  async #cartAction(scanId: string, op: () => Promise<{ ok: boolean; errors?: readonly Error[] }>): Promise<void> {
    const shopId = this.deps.storeRepository.shopId;
    const customerId = this.params.customerId;
    if (shopId === null || customerId === null) return;
    this.state.busyScanId.value = scanId;
    this.state.actionError.value = null;
    try {
      const r = await op();
      if (this.isDisposed) return;
      if (!r.ok) this.state.actionError.value = errorText(r.errors ?? []);
      // The server decides what the add or remove did to the cart, so both are read again.
      await Promise.all([this.#loadCart(shopId, customerId), this.#loadScans(shopId, customerId)]);
    } finally {
      if (!this.isDisposed) this.state.busyScanId.value = null;
    }
  }
}
